import { createContext, useState, useEffect, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { visitorService, type Participant } from '../../../shared/services/visitors/visitorService';

interface VisitorContextData {
  participants: Participant[];
  participant: Participant | null;
  eventTitle: string;
  page: number;
  limit: number;
  total: number;
  nameFilter: string;
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;
  setNameFilter: (name: string) => void;
  clearFilters: () => void;
  isLoadingParticipants: boolean;
  isLoadingParticipant: boolean;
  error: string | null;
  refetchParticipant: () => void;
  label_ingresso_inteiro?: string;
  label_ingresso_meia?: string;
  label_ingresso_estudante?: string;
  label_ingresso_crianca?: string;
}

interface VisitorProviderProps {
  children: ReactNode;
  eventId?: string;
  participantId?: string;
}

export const VisitorContext = createContext<VisitorContextData>({} as VisitorContextData);

export function VisitorProvider({ children, eventId, participantId }: VisitorProviderProps) {
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(15);
  const [nameFilter, setNameFilter] = useState('');

  // Reset page when eventId or filters change
  useEffect(() => {
    setPage(1);
  }, [eventId, nameFilter]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };
  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  };

  const handleNameFilterChange = (name: string) => {
    setNameFilter(name);
  };

  const clearFilters = () => {
    setNameFilter('');
  };

  const {
    data: response = { participants: [], eventTitle: '', page: 1, limit: 10, total: 0 },
    isLoading: isLoadingParticipants,
  } = useQuery({
    queryKey: ['participants', eventId, page, limit, nameFilter],
    queryFn: async () => {
      if (!eventId) return { participants: [], eventTitle: '', page: 1, limit: 15, total: 0 };
      try {
        return await visitorService.listParticipantsByEvent(eventId, page, limit, nameFilter || undefined);
      } catch (error: any) {
        setError(error.response?.data?.message || error.message || 'Erro ao buscar participantes');
        return { participants: [], eventTitle: '', page: 1, limit: 15, total: 0 };
      }
    },
    enabled: !!eventId,
  });

  const {
    data: participant = null,
    isLoading: isLoadingParticipant,
    refetch: refetchParticipant,
  } = useQuery({
    queryKey: ['participant', participantId],
    queryFn: async () => {
      if (!participantId) return null;
      try {
        return await visitorService.getById(participantId);
      } catch (error: any) {
        setError(error.response?.data?.message || error.message || 'Erro ao buscar participante');
        return null;
      }
    },
    enabled: !!participantId,
  });

  return (
    <VisitorContext.Provider
      value={{
        participants: response.participants,
        participant,
        eventTitle: response.eventTitle,
        page,
        limit,
        total: response.total,
        nameFilter,
        setPage: handlePageChange,
        setLimit: handleLimitChange,
        setNameFilter: handleNameFilterChange,
        clearFilters,
        isLoadingParticipants,
        isLoadingParticipant,
        error,
        refetchParticipant,
        label_ingresso_inteiro: response.label_ingresso_inteiro,
      label_ingresso_meia: response.label_ingresso_meia,
      label_ingresso_estudante: response.label_ingresso_estudante,
      label_ingresso_crianca: response.label_ingresso_crianca,
      }}
    >
      {children}
    </VisitorContext.Provider>
  );
}